import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Save, Send, Printer, ExternalLink, ListPlus, Copy, CircleDollarSign, Mail, Eye, CheckCircle2, Pencil, Link2, Receipt as ReceiptIcon } from "lucide-react";
import confetti from "canvas-confetti";
import ProductPicker from "@/components/ProductPicker";
import { PaymentMethodIcon, methodLabel } from "@/components/PaymentMethodIcon";

const STATUS_META = {
  draft:   { label: "Draft",   color: "#94A3B8", bg: "rgba(148,163,184,0.15)" },
  sent:    { label: "Sent",    color: "#38BDF8", bg: "rgba(56,189,248,0.15)" },
  paid:    { label: "Paid",    color: "#25D366", bg: "rgba(37,211,102,0.15)" },
  overdue: { label: "Overdue", color: "#F87171", bg: "rgba(248,113,113,0.15)" },
  void:    { label: "Void",    color: "#6B7280", bg: "rgba(107,114,128,0.15)" },
};
const fmt = (n, ccy) => new Intl.NumberFormat(undefined, { style: "currency", currency: ccy || "SGD" }).format(n || 0);
const todayIso = () => new Date().toISOString().slice(0, 10);
const addDays = (isoDate, n) => { const d = new Date(isoDate); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export default function InvoiceEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: profile } = useQuery({ queryKey: ["billing-profile"], queryFn: () => api.get("/billing-profile").then((r) => r.data) });
  const { data: existing } = useQuery({
    queryKey: ["invoice", id],
    queryFn: () => api.get(`/invoices/${id}`).then((r) => r.data),
    enabled: !isNew,
  });
  const { data: deals = [] } = useQuery({ queryKey: ["deals-simple"], queryFn: () => api.get("/deals").then((r) => r.data) });

  const [form, setForm] = useState(() => ({
    bill_to: { name: "", address: "", postal_code: "", phone: "", email: "" },
    issue_date: todayIso(),
    due_date: addDays(todayIso(), 14),
    currency: "SGD",
    line_items: [{ description: "", quantity: 1, price: 0, include_gst: true, include_service_fee: true, product_id: null }],
    gst_rate: 0.09,
    service_fee_pct: null,
    terms: profile?.default_terms || [],
    notes: profile?.default_notes || "",
    status: "draft",
    deal_id: null,
    contact_id: null,
  }));

  // hydrate from existing
  useEffect(() => {
    if (existing) setForm({ ...existing });
  }, [existing]);

  // initial hydrate for terms from profile once we have it
  useEffect(() => {
    if (isNew && profile && (!form.terms || form.terms.length === 0)) {
      setForm((f) => ({ ...f, terms: profile.default_terms || [] }));
    }
    if (isNew && profile && form.service_fee_pct == null && profile.default_service_fee_pct != null) {
      setForm((f) => ({ ...f, service_fee_pct: profile.default_service_fee_pct }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const totals = useMemo(() => {
    const items = form.line_items || [];
    let subtotal = 0, gst = 0, feeBase = 0;
    for (const li of items) {
      const amt = (Number(li.quantity) || 0) * (Number(li.price) || 0);
      subtotal += amt;
      if (li.include_gst) gst += amt * (form.gst_rate || 0);
      if (li.include_service_fee !== false) feeBase += amt;
    }
    const pct = Number(form.service_fee_pct) || 0;
    const fee = feeBase * (pct / 100);
    const round = (n) => Math.round(n * 100) / 100;
    return { subtotal: round(subtotal), gst: round(gst), fee: round(fee), total: round(subtotal + gst + fee) };
  }, [form.line_items, form.gst_rate, form.service_fee_pct]);

  const meta = STATUS_META[form.status] || STATUS_META.draft;

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = { ...form };
      // remove server-computed fields
      delete payload.subtotal; delete payload.gst_amount; delete payload.total;
      delete payload.paid_total; delete payload.balance_due;
      delete payload.payments; delete payload.public_slug; delete payload.invoice_number;
      delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload.created_by;
      delete payload.billing_profile_snapshot; delete payload.company_id; delete payload.division_id;
      return isNew
        ? api.post("/invoices", payload).then((r) => r.data)
        : api.patch(`/invoices/${id}`, payload).then((r) => r.data);
    },
    onSuccess: (data) => {
      toast.success(isNew ? `Invoice ${data.invoice_number} created` : "Saved");
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["invoice", data.id] });
      if (isNew) navigate(`/invoices/${data.id}`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  // Confetti + auto-celebrate when this invoice's status flips to paid.
  const [prevStatus, setPrevStatus] = useState(null);
  useEffect(() => {
    if (existing?.status === "paid" && prevStatus && prevStatus !== "paid") {
      // Fire two bursts of confetti from the corners for a satisfying celebration
      const fire = (originX) => confetti({
        particleCount: 90,
        spread: 65,
        startVelocity: 45,
        origin: { x: originX, y: 0.7 },
        colors: ["#25D366", "#38BDF8", "#FBBF24", "#F87171", "#A78BFA"],
      });
      fire(0.2); setTimeout(() => fire(0.8), 200);
      toast.success("Invoice paid — thank-you email sent automatically 🎉");
    }
    if (existing) setPrevStatus(existing.status);
  }, [existing?.status]);  // eslint-disable-line react-hooks/exhaustive-deps

  const setStatusMut = useMutation({
    mutationFn: (status) => api.patch(`/invoices/${id}`, { status }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invoice", id] }),
  });

  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { description: "", quantity: 1, price: 0, include_gst: true, include_service_fee: true, product_id: null }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, x) => x !== i) }));
  const setLine = (i, patch) => setForm((f) => ({ ...f, line_items: f.line_items.map((li, x) => x === i ? { ...li, ...patch } : li) }));
  const applyProductToLine = (i, product) => setLine(i, {
    product_id: product.id,
    description: product.description ? `${product.name} — ${product.description}` : product.name,
    price: product.unit_price,
    include_gst: product.include_gst !== false,
    include_service_fee: product.include_service_fee !== false,
  });

  const publicUrl = existing?.public_slug ? `${window.location.origin}/inv/${existing.public_slug}` : null;

  return (
    <div>
      <PageHeader
        overline={isNew ? "New" : existing?.invoice_number || "Invoice"}
        title={isNew ? "New Invoice" : `Invoice ${existing?.invoice_number || ""}`}
        subtitle={<div className="flex items-center gap-2"><Badge className="rounded-sm text-[10px]" style={{ backgroundColor: meta.bg, color: meta.color, borderColor: meta.color + "44" }}>{meta.label}</Badge>{existing?.paid_total ? <span className="text-xs text-muted-foreground">Paid {fmt(existing.paid_total, form.currency)} of {fmt(existing.total, form.currency)}</span> : null}</div>}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {!isNew && publicUrl && (
              <Button variant="outline" className="rounded-sm" onClick={() => { navigator.clipboard.writeText(publicUrl).then(() => toast.success("Public URL copied")).catch(() => toast.error("Copy failed")); }} data-testid="invoice-copy-url"><Copy className="h-4 w-4 mr-2" />Copy URL</Button>
            )}
            {!isNew && publicUrl && (
              <Button variant="outline" className="rounded-sm" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/public/inv/${existing?.public_slug}/pdf`, "_blank")} data-testid="invoice-download-pdf"><Printer className="h-4 w-4 mr-2" />Download PDF</Button>
            )}
            {!isNew && publicUrl && (
              <Button variant="outline" className="rounded-sm" onClick={() => window.open(publicUrl, "_blank")} data-testid="invoice-view-public"><ExternalLink className="h-4 w-4 mr-2" />Preview</Button>
            )}
            {!isNew && <SendInvoiceButton invoiceId={id} defaultTo={form.bill_to?.email} publicUrl={publicUrl} status={form.status} />}
            <Button className="rounded-sm bg-primary" onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid="invoice-save"><Save className="h-4 w-4 mr-2" />{isNew ? "Create invoice" : "Save"}</Button>
          </div>
        }
      />

      <div className="px-6 lg:px-10 pb-10 grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-6">
          {/* Bill To */}
          <Card className="rounded-sm bg-card border-border p-6" data-testid="invoice-bill-to-card">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3">Bill To</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Customer name</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={form.bill_to.name || ""} onChange={(e) => setForm((f) => ({ ...f, bill_to: { ...f.bill_to, name: e.target.value } }))} data-testid="invoice-bill-name" /></div>
              <div className="col-span-2"><Label>Address</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={form.bill_to.address || ""} onChange={(e) => setForm((f) => ({ ...f, bill_to: { ...f.bill_to, address: e.target.value } }))} data-testid="invoice-bill-address" /></div>
              <div><Label>Postal code</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={form.bill_to.postal_code || ""} onChange={(e) => setForm((f) => ({ ...f, bill_to: { ...f.bill_to, postal_code: e.target.value } }))} data-testid="invoice-bill-postal" /></div>
              <div><Label>Phone</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={form.bill_to.phone || ""} onChange={(e) => setForm((f) => ({ ...f, bill_to: { ...f.bill_to, phone: e.target.value } }))} data-testid="invoice-bill-phone" /></div>
              <div className="col-span-2"><Label>Email</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" type="email" value={form.bill_to.email || ""} onChange={(e) => setForm((f) => ({ ...f, bill_to: { ...f.bill_to, email: e.target.value } }))} data-testid="invoice-bill-email" /></div>
            </div>
          </Card>

          {/* Dates + link */}
          <Card className="rounded-sm bg-card border-border p-6">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Issue date</Label><Input type="date" className="rounded-sm mt-1.5 bg-secondary/40" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="invoice-issue-date" /></div>
              <div><Label>Due date</Label><Input type="date" className="rounded-sm mt-1.5 bg-secondary/40" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="invoice-due-date" /></div>
              <div>
                <Label>Link to deal <span className="text-[10px] text-muted-foreground uppercase tracking-widest">— optional</span></Label>
                <Select value={form.deal_id || "none"} onValueChange={(v) => setForm({ ...form, deal_id: v === "none" ? null : v })}>
                  <SelectTrigger className="rounded-sm mt-1.5 bg-secondary/40" data-testid="invoice-deal-picker"><SelectValue placeholder="Standalone" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Standalone (no deal)</SelectItem>
                    {deals.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>GST rate</Label>
                <Input type="number" step="0.01" className="rounded-sm mt-1.5 bg-secondary/40" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: parseFloat(e.target.value) || 0 })} data-testid="invoice-gst-rate" />
              </div>
            </div>
          </Card>

          {/* Line items */}
          <Card className="rounded-sm bg-card border-border p-6" data-testid="invoice-line-items">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Line items</div>
              <div className="flex items-center gap-2">
                {!isNew && (
                  <ConvertToTasksButton invoiceId={id} lineItems={form.line_items} defaultDealId={form.deal_id} deals={deals} />
                )}
                <Button variant="outline" size="sm" className="rounded-sm" onClick={addLine} data-testid="invoice-add-line"><Plus className="h-4 w-4 mr-1.5" />Add line</Button>
              </div>
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-[36px_1fr_70px_90px_50px_50px_90px_28px] gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-bold px-1">
                <div />
                <div>Description</div><div>Qty</div><div>Price</div><div title="Include GST">GST</div><div title="Include in service fee">Fee</div><div className="text-right">Amount</div><div />
              </div>
              {form.line_items.map((li, i) => {
                const amt = (Number(li.quantity) || 0) * (Number(li.price) || 0);
                return (
                  <div key={i} className="grid grid-cols-[36px_1fr_70px_90px_50px_50px_90px_28px] gap-2 items-start" data-testid={`invoice-line-${i}`}>
                    <div className="pt-1"><ProductPicker selectedId={li.product_id} onPick={(p) => applyProductToLine(i, p)} testId={`invoice-line-product-picker-${i}`} /></div>
                    <Textarea rows={2} className="rounded-sm bg-secondary/40" value={li.description} onChange={(e) => setLine(i, { description: e.target.value })} data-testid={`invoice-line-desc-${i}`} />
                    <Input type="number" step="0.01" className="rounded-sm bg-secondary/40" value={li.quantity} onChange={(e) => setLine(i, { quantity: parseFloat(e.target.value) || 0 })} data-testid={`invoice-line-qty-${i}`} />
                    <Input type="number" step="0.01" className="rounded-sm bg-secondary/40" value={li.price} onChange={(e) => setLine(i, { price: parseFloat(e.target.value) || 0 })} data-testid={`invoice-line-price-${i}`} />
                    <div className="flex items-center justify-center pt-2.5"><Checkbox checked={!!li.include_gst} onCheckedChange={(v) => setLine(i, { include_gst: !!v })} data-testid={`invoice-line-gst-${i}`} /></div>
                    <div className="flex items-center justify-center pt-2.5"><Checkbox checked={li.include_service_fee !== false} onCheckedChange={(v) => setLine(i, { include_service_fee: !!v })} data-testid={`invoice-line-fee-${i}`} /></div>
                    <div className="text-right pt-2.5 text-sm font-semibold">{fmt(amt, form.currency)}</div>
                    <button onClick={() => removeLine(i)} className="pt-2.5 text-muted-foreground hover:text-red-400" data-testid={`invoice-line-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Terms */}
          <Card className="rounded-sm bg-card border-border p-6" data-testid="invoice-terms-card">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3">Terms &amp; Conditions</div>
            <Textarea rows={8} className="rounded-sm bg-secondary/40" placeholder="One clause per line…" value={(form.terms || []).join("\n")} onChange={(e) => setForm({ ...form, terms: e.target.value.split("\n") })} data-testid="invoice-terms" />
            <div className="mt-3"><Label>Notes</Label><Textarea rows={3} className="rounded-sm mt-1.5 bg-secondary/40" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="invoice-notes" /></div>
          </Card>
        </div>

        {/* Sidebar: totals + payments */}
        <div className="space-y-4">
          <Card className="rounded-sm bg-card border-border p-6" data-testid="invoice-totals">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3">Totals</div>
            <div className="space-y-1.5 text-sm">
              <Row label="Subtotal" value={fmt(totals.subtotal, form.currency)} />
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span>Service fee</span>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    className="rounded-sm bg-secondary/40 h-7 w-16 text-xs"
                    value={form.service_fee_pct ?? ""}
                    onChange={(e) => setForm({ ...form, service_fee_pct: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
                    data-testid="invoice-service-fee-pct"
                    title="Overrides the division default for this invoice"
                  />
                  <span className="text-xs">%</span>
                </div>
                <div>{fmt(totals.fee, form.currency)}</div>
              </div>
              <Row label={`GST (${((form.gst_rate || 0) * 100).toFixed(1)}%)`} value={fmt(totals.gst, form.currency)} />
              <div className="h-px bg-border my-2" />
              <Row label="Total" value={fmt(totals.total, form.currency)} bold />
              {existing?.paid_total > 0 && <Row label="Paid" value={fmt(existing.paid_total, form.currency)} muted />}
              {existing && <Row label="Balance due" value={fmt(existing.balance_due, form.currency)} accent />}
            </div>
          </Card>

          {!isNew && <PaymentsCard invoice={existing} currency={form.currency} />}
          {!isNew && <EmailEventsCard invoice={existing} />}
          {!isNew && <PayNowDynamicCard invoiceId={id} balance={existing?.balance_due} />}

          {profile && (
            <Card className="rounded-sm bg-card border-border p-6" data-testid="invoice-payment-methods">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">Payment methods on invoice</div>
              <div className="text-xs text-muted-foreground space-y-1">
                {profile.bank_account_no && <div><b>{profile.bank_name}</b>: {profile.bank_account_no}</div>}
                {profile.paynow_uen && <div><b>PayNow UEN</b>: {profile.paynow_uen}</div>}
                {profile.paynow_qr_image && <img alt="PayNow QR" src={profile.paynow_qr_image} className="h-24 w-24 rounded-sm border border-border mt-2" data-testid="invoice-paynow-qr-preview" />}
                {!profile.bank_account_no && !profile.paynow_uen && (
                  <Link to="/settings?tab=billing" className="text-primary hover:underline">Add bank + PayNow details →</Link>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold, muted, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`${muted ? "text-muted-foreground" : ""} ${bold ? "font-bold" : ""}`}>{label}</span>
      <span className={`${accent ? "text-primary font-bold" : bold ? "font-black text-lg" : ""}`}>{value}</span>
    </div>
  );
}

function PaymentsCard({ invoice, currency }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // {id, ...} when editing
  const emptyForm = { amount: invoice?.balance_due || 0, method: "paynow", date: new Date().toISOString().slice(0, 10), reference: "", note: "" };
  const [form, setForm] = useState(emptyForm);
  useEffect(() => { if (!editing) setForm((f) => ({ ...f, amount: invoice?.balance_due || 0 })); }, [invoice?.balance_due, editing]);

  const record = useMutation({
    mutationFn: () => api.post(`/invoices/${invoice.id}/payments`, form),
    onSuccess: () => { setOpen(false); setForm(emptyForm); qc.invalidateQueries({ queryKey: ["invoice", invoice.id] }); toast.success("Payment recorded"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not record payment"),
  });
  const patch = useMutation({
    mutationFn: () => api.patch(`/invoices/${invoice.id}/payments/${editing.id}`, form),
    onSuccess: () => { setOpen(false); setEditing(null); setForm(emptyForm); qc.invalidateQueries({ queryKey: ["invoice", invoice.id] }); toast.success("Payment updated"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Update failed"),
  });
  const del = useMutation({
    mutationFn: (pid) => api.delete(`/invoices/${invoice.id}/payments/${pid}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoice", invoice.id] }); toast.success("Payment removed"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Delete failed"),
  });
  const emailReceipt = useMutation({
    mutationFn: (pid) => api.post(`/invoices/${invoice.id}/payments/${pid}/email-receipt`, {}),
    onSuccess: (r) => { toast.success(`Receipt emailed to ${r.data.to}`); qc.invalidateQueries({ queryKey: ["invoice", invoice.id] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Email failed"),
  });

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ amount: p.amount, method: p.method || "paynow", date: p.date, reference: p.reference || "", note: p.note || "" });
    setOpen(true);
  };
  const copyReceiptLink = (p) => {
    if (!p.public_slug) { toast.error("Receipt link not ready — reload to backfill"); return; }
    const url = `${window.location.origin}/receipt/${p.public_slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Receipt link copied");
  };

  return (
    <Card className="rounded-sm bg-card border-border p-6" data-testid="invoice-payments-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Payments</div>
        <Button size="sm" variant="outline" className="rounded-sm" onClick={openNew} data-testid="invoice-record-payment"><CircleDollarSign className="h-3.5 w-3.5 mr-1.5" />Record</Button>
      </div>
      <div className="space-y-2">
        {(invoice?.payments || []).length === 0 && <div className="text-xs text-muted-foreground">No payments recorded yet.</div>}
        {(invoice?.payments || []).map((p) => (
          <div key={p.id} className="p-2 rounded-sm bg-secondary/20 border border-border" data-testid={`invoice-payment-${p.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1 flex items-start gap-2.5">
                <PaymentMethodIcon method={p.method} className="h-4 w-4 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">{fmt(p.amount, currency)}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{methodLabel(p.method)} · {p.date}</div>
                  {p.reference && <div className="text-[10px] text-muted-foreground truncate">ref: {p.reference}</div>}
                  {p.last_receipt_sent_at && (
                    <div className="text-[10px] text-green-500/80 flex items-center gap-1 mt-0.5"><CheckCircle2 className="h-2.5 w-2.5" />Receipt sent to {p.last_receipt_sent_to}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                <button onClick={() => copyReceiptLink(p)} className="p-1 text-muted-foreground hover:text-primary" title="Copy receipt link" data-testid={`invoice-payment-link-${p.id}`}><Link2 className="h-3.5 w-3.5" /></button>
                <button
                  onClick={() => {
                    if (!(invoice?.bill_to || {}).email) { toast.error("Add a customer email to the invoice first"); return; }
                    emailReceipt.mutate(p.id);
                  }}
                  disabled={emailReceipt.isPending}
                  className="p-1 text-muted-foreground hover:text-primary disabled:opacity-40"
                  title="Email receipt to customer"
                  data-testid={`invoice-payment-email-${p.id}`}
                ><Mail className="h-3.5 w-3.5" /></button>
                <button onClick={() => openEdit(p)} className="p-1 text-muted-foreground hover:text-primary" title="Edit payment" data-testid={`invoice-payment-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => { if (window.confirm("Delete this payment? The invoice will re-open if it was marked paid.")) del.mutate(p.id); }} className="p-1 text-muted-foreground hover:text-red-400" title="Delete payment" data-testid={`invoice-payment-del-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent className="rounded-sm" data-testid="payment-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit payment" : "Record payment"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Amount</Label><Input type="number" step="0.01" className="rounded-sm mt-1.5 bg-secondary/40" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} data-testid="payment-amount" /></div>
            <div><Label>Method</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger className="rounded-sm mt-1.5 bg-secondary/40" data-testid="payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="paynow">PayNow</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" className="rounded-sm mt-1.5 bg-secondary/40" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="payment-date" /></div>
            <div><Label>Reference (optional)</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="payment-reference" /></div>
            <div><Label>Note (optional)</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={() => (editing ? patch.mutate() : record.mutate())}
              className="rounded-sm bg-primary"
              disabled={(editing ? patch.isPending : record.isPending) || form.amount <= 0}
              data-testid="payment-submit"
            >{editing ? (patch.isPending ? "Saving…" : "Save changes") : (record.isPending ? "Recording…" : "Record")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ConvertToTasksButton({ invoiceId, lineItems, defaultDealId, deals }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({});
  const [dealId, setDealId] = useState(defaultDealId || "");
  const [dueDate, setDueDate] = useState("");
  const qc = useQueryClient();
  useEffect(() => { setDealId(defaultDealId || ""); }, [defaultDealId]);

  const mut = useMutation({
    mutationFn: () => api.post(`/invoices/${invoiceId}/convert-to-tasks`, {
      line_indexes: Object.keys(selected).filter((k) => selected[k]).map(Number),
      deal_id: dealId || null,
      due_date: dueDate || null,
    }),
    onSuccess: (r) => {
      setOpen(false); setSelected({}); qc.invalidateQueries({ queryKey: ["all-tasks"] });
      toast.success(`Created ${r.data.created.length} task(s)`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Convert failed"),
  });

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <>
      <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setOpen(true)} data-testid="invoice-convert-tasks-btn"><ListPlus className="h-4 w-4 mr-1.5" />To tasks</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="convert-tasks-dialog">
          <DialogHeader><DialogTitle>Create tasks from line items</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Deal (tasks live under deals)</Label>
              <Select value={dealId || "none"} onValueChange={(v) => setDealId(v === "none" ? "" : v)}>
                <SelectTrigger className="rounded-sm mt-1.5 bg-secondary/40" data-testid="convert-tasks-deal"><SelectValue placeholder="Pick a deal" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— pick a deal —</SelectItem>
                  {deals.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Due date (optional)</Label><Input type="date" className="rounded-sm mt-1.5 bg-secondary/40" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="convert-tasks-due" /></div>
            <div>
              <Label>Line items <span className="text-[10px] text-muted-foreground uppercase tracking-widest">— pick which to convert</span></Label>
              <div className="space-y-1.5 mt-1.5 max-h-56 overflow-y-auto">
                {lineItems.map((li, i) => (
                  <label key={i} className="flex items-center gap-2 p-2 rounded-sm border border-border bg-secondary/20 hover:bg-secondary/40 cursor-pointer" data-testid={`convert-line-${i}`}>
                    <Checkbox checked={!!selected[i]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [i]: !!v }))} />
                    <span className="text-sm truncate">{li.description || `Line ${i + 1}`}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => mut.mutate()} disabled={!dealId || selectedCount === 0 || mut.isPending} className="rounded-sm bg-primary" data-testid="convert-tasks-submit">Create {selectedCount} task{selectedCount === 1 ? "" : "s"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SendInvoiceButton({ invoiceId, defaultTo, publicUrl, status }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ to: defaultTo || "", cc: "", subject: "", message: "" });
  const qc = useQueryClient();
  useEffect(() => { setForm((f) => ({ ...f, to: defaultTo || f.to })); }, [defaultTo]);

  const send = useMutation({
    mutationFn: () => api.post(`/invoices/${invoiceId}/send`, {
      to: form.to,
      cc: form.cc ? form.cc.split(",").map((s) => s.trim()).filter(Boolean) : null,
      subject: form.subject || null,
      message: form.message || null,
    }),
    onSuccess: () => {
      setOpen(false);
      toast.success(`Invoice emailed to ${form.to}`);
      // Refetch invoice so the Email delivery card updates in place with the new "sent" event.
      qc.invalidateQueries({ queryKey: ["invoice", invoiceId] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
    onError: (e) => {
      const detail = e.response?.data?.detail || "";
      const isSmtp = /smtp_not_configured/i.test(detail);
      if (isSmtp) {
        toast.error("Your mailbox isn't set up yet", {
          description: "Add your SMTP mailbox in Settings → Email so LeadifyCRM can send invoices on your behalf.",
          action: {
            label: "Open Settings",
            onClick: () => {
              window.location.assign("/settings?tab=email");
            },
          },
          duration: 8000,
        });
      } else {
        toast.error(detail || "Send failed. Check Settings → Email → My Mailbox.");
      }
      // Even on failure the backend pushes a send_failed event — refetch so the sidebar shows it.
      qc.invalidateQueries({ queryKey: ["invoice", invoiceId] });
    },
  });

  return (
    <>
      <Button variant="outline" className="rounded-sm" onClick={() => setOpen(true)} data-testid="invoice-email-btn">
        <Mail className="h-4 w-4 mr-2" />
        {status === "draft" ? "Email invoice" : "Resend email"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="email-invoice-dialog">
          <DialogHeader><DialogTitle>Email Invoice</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>To</Label><Input type="email" className="rounded-sm mt-1.5 bg-secondary/40" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} data-testid="email-to" placeholder="customer@example.com" /></div>
            <div><Label>Cc <span className="text-[10px] text-muted-foreground uppercase tracking-widest">— comma-separated, optional</span></Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={form.cc} onChange={(e) => setForm({ ...form, cc: e.target.value })} data-testid="email-cc" /></div>
            <div><Label>Subject <span className="text-[10px] text-muted-foreground uppercase tracking-widest">— optional</span></Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="email-subject" placeholder="Auto-generated if left blank" /></div>
            <div><Label>Message <span className="text-[10px] text-muted-foreground uppercase tracking-widest">— optional, plain text</span></Label><Textarea rows={6} className="rounded-sm mt-1.5 bg-secondary/40" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="email-message" placeholder="Auto-generated if left blank. Includes the online view link + amount due." /></div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground p-2 rounded-sm bg-secondary/30 border border-border">
              Sent from your SMTP mailbox · Public link included · Open receipts tracked
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-sm bg-primary" onClick={() => send.mutate()} disabled={!form.to || send.isPending} data-testid="email-send">
              <Send className="h-4 w-4 mr-2" />Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function EmailEventsCard({ invoice }) {
  const events = invoice?.email_events || [];
  if (events.length === 0) return null;
  const iconFor = (ev) => ev === "sent" ? Send : ev === "opened" ? Eye : ev === "thank_you_sent" ? CheckCircle2 : Mail;
  const colorFor = (ev) => ev === "sent" ? "#38BDF8" : ev === "opened" ? "#25D366" : ev === "thank_you_sent" ? "#A78BFA" : "#94A3B8";
  const labelFor = (ev) => ({ sent: "Sent to customer", opened: "Customer opened email", thank_you_sent: "Thank-you sent", thank_you_failed: "Thank-you send failed", send_failed: "Send failed" }[ev] || ev);
  return (
    <Card className="rounded-sm bg-card border-border p-6" data-testid="email-events-card">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3">Email delivery</div>
      <div className="space-y-2">
        {events.slice().reverse().slice(0, 8).map((e) => {
          const Icon = iconFor(e.event);
          const color = colorFor(e.event);
          return (
            <div key={e.id} className="flex items-start gap-2" data-testid={`email-event-${e.event}`}>
              <div className="h-6 w-6 rounded-sm flex items-center justify-center flex-none mt-0.5" style={{ background: color + "20", color }}>
                <Icon className="h-3 w-3" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold">{labelFor(e.event)}</div>
                <div className="text-[10px] text-muted-foreground">{new Date(e.at).toLocaleString()} {e.to ? `· ${e.to}` : ""}</div>
                {e.reason && <div className="text-[10px] text-red-400 mt-0.5">{e.reason}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function PayNowDynamicCard({ invoiceId, balance }) {
  const { data } = useQuery({
    queryKey: ["paynow-dynamic", invoiceId, balance],
    queryFn: () => api.get(`/invoices/${invoiceId}/paynow-qr`).then((r) => r.data).catch(() => null),
    enabled: !!invoiceId && !!balance && balance > 0,
    staleTime: 60 * 1000,
  });
  if (!data?.data_uri) return null;
  return (
    <Card className="rounded-sm bg-card border-border p-6" data-testid="paynow-dynamic-card">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">Dynamic PayNow QR</div>
      <div className="font-display text-sm font-extrabold mb-3">Customer scans → amount pre-filled</div>
      <img src={data.data_uri} alt="PayNow QR" className="w-full aspect-square rounded-sm border border-border bg-white p-3" data-testid="paynow-dynamic-qr" />
      <div className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground text-center">
        SGD {data.amount?.toFixed(2)} · ref {data.reference}
      </div>
    </Card>
  );
}

